import { useEffect, useState } from 'react';
import { posBusinessDriverListSchema, type PosBusinessDriverListItem } from '@easyflow/shared';
import { apiClient, formatApiErrorMessage, isApiError, sectionLoadDeadline, type ApiError } from '@/lib/api';

export type CampaignBusinessDriversState =
  | { status: 'loading' }
  | { status: 'ready'; drivers: PosBusinessDriverListItem[] }
  | { status: 'forbidden' | 'error'; message: string };

export function useCampaignBusinessDrivers(open: boolean, campaignId: string | undefined) {
  const [state, setState] = useState<CampaignBusinessDriversState>({ status: 'loading' });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!open) return;
    let current = true;
    setState({ status: 'loading' });
    const load = async () => {
      try {
        const response = await apiClient.get<unknown>('/pos/business-drivers', { signal: sectionLoadDeadline() });
        if (!response.success) {
          const error: ApiError = new Error(response.message || response.error || 'Impossibile caricare i driver commerciali.');
          error.requestId = response.request_id;
          throw error;
        }
        const parsed = posBusinessDriverListSchema.safeParse(response.data);
        if (!parsed.success) throw new Error('Risposta del Catalogo POS non valida. Riprova o contatta l’assistenza.');
        if (current) setState({ status: 'ready', drivers: parsed.data });
      } catch (error: unknown) {
        if (current) setState({
          status: isApiError(error) && error.status === 403 ? 'forbidden' : 'error',
          message: formatApiErrorMessage(error, 'Impossibile caricare i driver commerciali.'),
        });
      }
    };
    void load();
    return () => { current = false; };
  }, [open, campaignId, attempt]);

  return { state, retry: () => setAttempt(previous => previous + 1) };
}
